import React from 'react'
import PropTypes from 'prop-types'
import {
  FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { JOB_START_MODE } from '../../FabricationOrderJobReference/PlannedJobs/utils/jobStatus'
import { PREPARATION_LABEL_COLORS } from '../PreparationLabel'

// Historia 120 (Jupiter, paquete P5, S120) — EXTRACTED verbatim from
// `PlannedJobs/components/JobStatusChangeDialog/JobStatusChangeDialog.jsx` (`MODE_OPTION:34-46` and
// the `RadioGroup` block `:192-270` before this refactor), so the individual start dialog (ONE job)
// and the batch mode question (`BatchStartModeDialog`, S120-S123) render the EXACT SAME two touch
// cards -- not a lookalike copy that could drift the next time someone tweaks a colour.
//
// 🔒 Pure refactor (plan-jupiter.md §"S120"): `JobStatusChangeDialog`'s own behaviour/tests
// (`JobStatusChangeDialog.modeCards.test.jsx`, `.startMode.test.jsx`, etc.) must stay green,
// UNCHANGED, after this extraction -- this component reproduces its exact DOM (same FormControl,
// same hidden FormLabel/aria-labelledby, same two FormControlLabel cards, same radio values/names).
//
// S107 -- exactly TWO options and no third one. The preparation card wears the SAME contracted
// colours as `PreparationLabel` (RN50): the operator picks preparation here and then sees that exact
// purple on the station header. Execution keeps the app's own success green. `accent`/`fill` are
// theme callbacks so execution can follow the palette instead of hardcoding a colour.
const MODE_OPTION = {
  [JOB_START_MODE.PREPARATION]: {
    labelKey: 'Preparation',
    Icon: BuildOutlinedIcon,
    accent: () => PREPARATION_LABEL_COLORS.border,
    fill: () => PREPARATION_LABEL_COLORS.fill
  },
  [JOB_START_MODE.EXECUTION]: {
    labelKey: 'Execution',
    Icon: PlayArrowRoundedIcon,
    accent: (theme) => theme.palette.success.main,
    fill: (theme) => theme.palette.success.main
  }
}

/**
 * @param {Object} props
 * @param {string} props.value One of `JOB_START_MODE` -- the currently selected mode.
 * @param {Function} props.onChange Called with the new `JOB_START_MODE` value when a card is picked.
 * @param {string} [props.legendId='job-start-mode-label'] `id` shared by the hidden legend and the
 *   `RadioGroup`'s `aria-labelledby` -- kept overridable so two instances never collide in the same
 *   DOM (the individual dialog and the batch mode dialog never mount together, but tests might).
 */
const BatchStartModeCards = ({ value, onChange, legendId = 'job-start-mode-label' }) => {
  const { t_ } = useSmartTranslation()

  return (
    <FormControl fullWidth>
      {/* The dialog title already asks the question, so repeating it above the cards is just noise.
          The legend stays for screen readers. */}
      <FormLabel id={legendId} sx={visuallyHidden}>
        {t_('Target Status')}
      </FormLabel>
      <RadioGroup
        row
        aria-labelledby={legendId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{ gap: 1, flexWrap: 'nowrap' }}
      >
        {Object.entries(MODE_OPTION).map(([mode, { labelKey, Icon, accent, fill }]) => {
          const selected = value === mode
          return (
            <FormControlLabel
              key={mode}
              value={mode}
              // The radio itself stays in the accessibility tree (clipped, not display:none) so the
              // control keeps its native semantics, keyboard behaviour and testability -- the card is
              // the visual affordance, not a replacement for the input.
              control={<Radio sx={visuallyHidden} />}
              disableTypography
              sx={(theme) => ({
                flex: 1,
                m: 0,
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                cursor: 'pointer',
                userSelect: 'none',
                border: 2,
                borderStyle: 'solid',
                borderColor: selected ? accent(theme) : theme.palette.divider,
                // A tint rather than the flat fill: at full strength the purple fights the white
                // label for contrast on this dark panel.
                backgroundColor: selected
                  ? alpha(fill(theme), 0.28)
                  : 'transparent',
                transition: theme.transitions.create(['border-color', 'background-color'], {
                  duration: theme.transitions.duration.shortest
                }),
                '&:hover': {
                  borderColor: selected ? accent(theme) : theme.palette.text.disabled,
                  backgroundColor: selected
                    ? alpha(fill(theme), 0.36)
                    : alpha(theme.palette.action.hover, 0.6)
                },
                // Keyboard users get the focus ring the clipped input would otherwise take away with it.
                '&:focus-within': {
                  outline: `2px solid ${accent(theme)}`,
                  outlineOffset: 2
                }
              })}
              label={
                <Stack alignItems='center' spacing={0.5} sx={{ width: '100%' }}>
                  <Icon
                    sx={(theme) => ({
                      fontSize: 24,
                      color: selected ? accent(theme) : theme.palette.text.disabled
                    })}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      color: selected ? 'text.primary' : 'text.secondary'
                    }}
                  >
                    {t_(labelKey)}
                  </Typography>
                </Stack>
              }
            />
          )
        })}
      </RadioGroup>
    </FormControl>
  )
}

BatchStartModeCards.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  legendId: PropTypes.string
}

export default BatchStartModeCards
